/**
 * The site's "Platform Prerequisites" card: what JBS depends on in the site
 * cluster (storage, ingress, load balancer, GPU and dataplane networking).
 *
 * Two installers own them, per site (#805 B3):
 *  - Helm path: deployment-service runs each step and records it in
 *    `site.prerequisites`, one row per step.
 *  - Operator path: the jss-operator in the site cluster installs them and
 *    reports each as a condition on its CR. The site carries
 *    `operatorStatus` (JSS's handoff to the operator) and never writes
 *    `prerequisites`, which would otherwise sit at its column default of
 *    every step pending. Per-component state comes from the
 *    PREREQUISITES_READY readiness check, which reads the CR.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, Loader2, RefreshCw, Bot } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { cn } from '@/lib/utils';
import { sitesApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { parseApiError } from '@/lib/apiError';
import { readinessDocsUrl, siteReadinessQueryKey } from '@/lib/readiness';
import {
  describeOperatorHandoff,
  operatorComponentLines,
  prerequisiteSteps,
  type OperatorHandoffTone,
} from '@/pages/prerequisiteSteps';
import type { PrerequisiteStatus, PrerequisiteStepStatus, Site, SiteOperatorStatus } from '@/types';

function PrerequisiteStepIcon({ status }: { status: PrerequisiteStepStatus }) {
  switch (status) {
    case 'ok': return <Check size={12} className="text-status-healthy" />;
    case 'skipped': return <span className="text-silver-muted text-xs">--</span>;
    case 'failed': return <X size={12} className="text-status-error" />;
    default: return <Loader2 size={12} className="text-silver-muted animate-spin" />;
  }
}

/** A failing prerequisite also blocks the JBS install (#796): say so, and link the check that names the fix (#804). */
function BlocksJbsNote() {
  return (
    <p className="text-[10px] text-status-warning mt-1">
      JBS can&apos;t be installed while a prerequisite is failing. See{' '}
      <a
        href={readinessDocsUrl('PREREQUISITES_READY')}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono underline hover:text-status-warning/80"
      >
        PREREQUISITES_READY
      </a>{' '}
      under Ready for JBS for the cause and the fix.
    </p>
  );
}

function useReprovision(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => sitesApi.reprovision(siteId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/** POST /sites/:id/reprovision is SiteOperator at the BFF; ungated, it 403'd below that (JSS#970). */
function ReprovisionButton({ mutation }: { mutation: ReturnType<typeof useReprovision> }) {
  return (
    <RoleGate role={Role.SiteOperator}>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        data-testid="prerequisites-reprovision"
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gold/10 text-gold rounded-lg hover:bg-gold/20 transition-colors disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        Reprovision
      </button>
    </RoleGate>
  );
}

function ReprovisionError({ mutation }: { mutation: ReturnType<typeof useReprovision> }) {
  if (!mutation.isError) return null;
  return (
    <p className="text-[10px] text-status-error mt-1">Reprovision failed: {parseApiError(mutation.error).message}</p>
  );
}

// ---- Helm path ----

function HelmPrerequisites({ siteId, prerequisites }: { siteId: string; prerequisites: PrerequisiteStatus }) {
  const reprovision = useReprovision(siteId);
  const steps = prerequisiteSteps(prerequisites);
  const allDone = steps.every(([, , status]) => status === 'ok' || status === 'skipped');
  const hasFailed = steps.some(([, , status]) => status === 'failed');

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5" data-testid="prerequisites-card-helm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-silver-bright">Platform Prerequisites</h3>
        {!allDone && <ReprovisionButton mutation={reprovision} />}
      </div>
      <div className="space-y-2">
        {steps.map(([key, label, status]) => {
          const error = prerequisites.failureMessages?.[key];
          return (
            <div key={key} className="flex items-center gap-2">
              <PrerequisiteStepIcon status={status} />
              <span className={cn('text-xs', status === 'failed' ? 'text-status-error' : status === 'skipped' ? 'text-silver-muted' : 'text-silver-bright')}>
                {label}
              </span>
              {error && (
                <span className="text-[10px] text-status-error/70 truncate ml-auto max-w-[200px]" title={error}>
                  {error.split(':').pop()?.trim() || error}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {prerequisites.lastRunAt && (
        <p className="text-[10px] text-silver-muted mt-3">
          Last run: {new Date(prerequisites.lastRunAt).toLocaleString()}
        </p>
      )}
      {hasFailed && !reprovision.isPending && <BlocksJbsNote />}
      <ReprovisionError mutation={reprovision} />
    </div>
  );
}

// ---- Operator path ----

const HANDOFF_TONE: Record<OperatorHandoffTone, string> = {
  healthy: 'bg-status-healthy/20 text-status-healthy',
  progress: 'bg-status-warning/20 text-status-warning',
  error: 'bg-status-error/20 text-status-error',
};

function OperatorPrerequisites({ siteId, status }: { siteId: string; status: SiteOperatorStatus }) {
  const reprovision = useReprovision(siteId);
  // Same query (key and cadence) as the "Ready for JBS" panel next to this
  // card, so it is one request, not two.
  const { data: readiness } = useQuery({
    queryKey: siteReadinessQueryKey(siteId),
    queryFn: () => sitesApi.getReadiness(siteId),
    refetchInterval: 30_000,
    staleTime: 30_000,
  });

  const handoff = describeOperatorHandoff(status, Date.now());
  const check = readiness?.checks.find((c) => c.id === 'PREREQUISITES_READY');
  const lines = check && check.status !== 'pass' ? operatorComponentLines(check) : [];
  const failing = handoff.tone === 'error' || check?.status === 'fail';

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5" data-testid="prerequisites-card-operator">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-silver-bright">Platform Prerequisites</h3>
          <p className="text-[10px] text-silver-muted mt-0.5">
            Installed and kept up to date by the Jeket platform operator in this site&apos;s cluster.
          </p>
        </div>
        {(handoff.tone !== 'healthy' || check?.status === 'fail') && <ReprovisionButton mutation={reprovision} />}
      </div>

      {/* JSS's handoff to the operator: credentials, the operator itself, the spec. */}
      <div className="space-y-1.5" data-testid="operator-handoff">
        <div className="flex items-center gap-2">
          <Bot size={14} className="text-silver-muted shrink-0" />
          <span className="text-xs text-silver-bright">Operator</span>
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium', HANDOFF_TONE[handoff.tone])}>
            {handoff.label}
          </span>
        </div>
        <p className="text-[11px] text-silver-muted">{handoff.detail}</p>
        {handoff.error && (
          <pre
            className="text-[11px] text-status-error whitespace-pre-wrap break-words font-mono bg-status-error/5 border border-status-error/20 rounded p-2"
            data-testid="operator-handoff-error"
          >
            {handoff.error}
          </pre>
        )}
      </div>

      {/* The components, as the operator reports them (PREREQUISITES_READY reads its CR). */}
      <div className="mt-4 space-y-1.5" data-testid="operator-components">
        <h4 className="text-[10px] font-medium text-silver-muted uppercase tracking-wider">Components</h4>
        {!check ? (
          <p className="text-[11px] text-silver-muted">Checking the operator&apos;s report…</p>
        ) : check.status === 'pass' ? (
          <div className="flex items-center gap-2">
            <Check size={12} className="text-status-healthy" />
            <span className="text-xs text-silver-bright">{check.summary}</span>
          </div>
        ) : (
          <>
            <p className={cn('text-xs', check.status === 'fail' ? 'text-status-error' : 'text-status-warning')}>{check.summary}</p>
            {lines.map((l, i) => (
              <div key={i} className="flex items-start gap-2" data-testid="operator-component">
                <X size={12} className="text-status-error shrink-0 mt-0.5" />
                <span className="text-xs text-silver-bright shrink-0">{l.label}</span>
                {l.text && (
                  <span className="text-[10px] text-status-error/80 break-words min-w-0" title={l.text}>
                    {l.text}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {status.operatorImage && (
        <p className="text-[10px] text-silver-muted mt-3 font-mono break-all" title="The operator build installed on this site">
          {status.operatorImage}
        </p>
      )}
      {failing && !reprovision.isPending && <BlocksJbsNote />}
      {reprovision.data?.queued && (
        <p className="text-[10px] text-silver-muted mt-1">
          The site is busy; the operator pass is queued and runs within a minute.
        </p>
      )}
      <ReprovisionError mutation={reprovision} />
    </div>
  );
}

export function PrerequisitesCard({ site }: { site: Pick<Site, 'id' | 'prerequisites' | 'operatorStatus'> }) {
  if (site.operatorStatus) return <OperatorPrerequisites siteId={site.id} status={site.operatorStatus} />;
  if (!site.prerequisites) return null;
  return <HelmPrerequisites siteId={site.id} prerequisites={site.prerequisites} />;
}

export default PrerequisitesCard;
